import {
  Alert,
  AutocompleteRenderGetTagProps,
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { AdvancedTagMappingPopupDialog } from './style';
import {
  FormProvider,
  RHFAutocomplete,
  RHFSelect,
  RHFTextField,
} from 'src/components/minimals/form';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { filterOptions, upperSnakeCaseToTitleCase, validateValueChange } from 'src/utilities';
import { LoadingButton } from '@mui/lab';
import {
  ALLOWED_INPUTS,
  CONTROLLABLE,
  CustomErrors,
  DATA_TYPES,
  DataType,
  DataTypeState,
  FormattedSubmitData,
  ModalProps,
  ModalTableRowData,
  RELEVANT_MODES,
  RelevantModeResponseBody,
  RelevantModeState,
  TagType,
  UpdatedInput,
  UserDefinedValue,
  UserDefinedValueWithCustomId,
} from './types';
import { AdvancedTagMappingService } from 'src/services/advancedTagMapping/advanced-tag-mapping.service';
import { useAuthentication } from 'src/features/authentication/context';
import { AdvancedTagMappingTable } from './AdvancedTagMappingTable';
import { getUpdatedFormValues } from './utils/get-updated-form-values';
import {
  getEntireRelevantModes,
  getFormattedData,
  getRelevantModeIds,
  removeDynamicFields,
  calculateGridPropsRelevantMode,
  calculateGridPropsValues,
} from './utils';
import { AccessRoles } from 'src/features/user-account-details/types';
import { GatewayInput } from 'src/types/gateways';
import { AdvancedTagMapping } from '../../types';
import { SimpleTooltip } from '../Tooltips';
import { AdvancedTagMappingSkeleton } from './AdvancedTagMappingSkeleton';
import { useSnackbar } from 'notistack';
import { areTableValuesUnique, validateMinMaxValueChange } from './validation';
import {
  ATM_SUBMIT_FAILURE_MESSAGE,
  ATM_SUBMIT_SUCCESS_MESSAGE,
  MANDATORY_FIELDS_VALIDATION_ERROR_MESSAGE,
  MAX_DECIMAL_FIELD_TOOLTIP,
  RANGE_FIELD_TOOLTIP,
} from './config';
import { useResponsive } from 'src/hooks/useResponsive';
import { DropdownData } from 'src/types/dropdown-data';
import { Controller } from 'src/types/controllers/controller';
import { LabelOptionsForm } from './components/LabelOptionsForm';
import { LabelingTypeEnum } from 'src/types';

export const AdvancedTagMappingModal = ({
  modalState,
  onClose,
  gatewayId,
  disableControllerFields = false,
  controllableHelperText,
  isStreamMLite = false,
  isIntegralert = false,
}: ModalProps) => {
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
    getCurrentRole,
  } = useAuthentication();
  const [currentInput, setCurrentInput] = useState<{ value: GatewayInput | null; loaded: boolean }>(
    { value: null, loaded: false }
  );
  const [showRelevantMode, setShowRelevantMode] = useState<boolean>(false);
  const [relevantModeState, setRelevantModeState] = useState<RelevantModeState>({
    labels: [],
    values: [],
    loaded: false,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [rowData, setRowData] = useState<ModalTableRowData[]>([{ index: 0 }]);
  const [dataHasFullyLoaded, setDataHasFullyLoaded] = useState<boolean>(false);
  const [dataTypeState, setDataTypeState] = useState<DataTypeState>({
    value: { values: [], labels: [] },
    loaded: false,
  });

  const isMobile = useResponsive('down', 'sm');
  const theme = useTheme();

  const [tableValueErrors, setTableValueErrors] = useState<number[][]>([]);
  const [customErrors, setCustomErrors] = useState<CustomErrors>({});

  const allowedInputs: DropdownData = require('./data/allowed-inputs.json');
  const dataTypes: DataType = require('./data/data-types.json');
  const controllable: DropdownData = require('./data/controllable.json');

  const hasReadAccess =
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_MANAGER ||
    getCurrentRole(customerId, siteId) === AccessRoles.SITE_EMPLOYEE;

  const noControllers = modalState?.controllers?.length === 0;
  const noControllersHelperText =
    'To enable control, a Controller must be added under Settings/Controllers.';

  const [formData, setFormData] = useState<AdvancedTagMapping>({
    controller: '',
    allowedInputs: ALLOWED_INPUTS.PREDEFINED,
    dataType: DATA_TYPES.INTEGER,
    controllable: CONTROLLABLE.NO,
    relevantMode: [RELEVANT_MODES.DO_NOT_RESTRICT],
    minValue: '',
    maxValue: '',
    maxDecimal: '',
    labellingType: LabelingTypeEnum.LABEL,
    fallbackValue: 'Undefined',
  });

  const [controllerType, setControllerType] = useState<string | null>(null);

  const methods = useForm<AdvancedTagMapping>({
    defaultValues: formData,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = methods;

  const values = watch();

  const controllableValue = watch('controllable');
  const isControllable = controllableValue === 'YES';

  // For Stream Lite gateways and IntegrAlert data sources the Range allowed input is not supported.
  const allowedInputsValues =
    isStreamMLite || isIntegralert
      ? allowedInputs.values.filter((value) => value !== ALLOWED_INPUTS.RANGE)
      : allowedInputs.values;

  const getRelevantModeValuesFromUserDefinedValues = useCallback(
    (
      data: RelevantModeResponseBody[],
      labels: string[],
      values: UserDefinedValueWithCustomId[]
    ) => {
      data.forEach((obj) => {
        obj.userDefinedValues.forEach((userDefinedValue) => {
          const label =
            data.length === 1
              ? `${userDefinedValue.value} - ${userDefinedValue.name}`
              : `${obj.displayName} - ${userDefinedValue.value} - ${userDefinedValue.name}`;

          labels.push(label);

          values.push({
            id: `${obj.tagId}_${userDefinedValue.value}`,
            ...userDefinedValue,
            tagId: obj.tagId,
          });
        });

        if (obj.maximumValue !== null && obj.minimumValue !== null) {
          for (let index = obj.minimumValue; index <= obj.maximumValue; index++) {
            const value = index;
            const label = data.length === 1 ? value.toString() : `${obj.displayName} - ${value}`;
            labels.push(label);

            values.push({
              id: `${obj.tagId}_${value}`,
              value,
              category: null,
              name: null,
              index: undefined,
              gatewayId,
              tagId: obj.tagId,
            });
          }
        }
      });
    },
    [gatewayId]
  );

  useEffect(() => {
    if (!modalState.open) return;
    if (
      !customerId ||
      !siteId ||
      !gatewayId ||
      values.controller === '' ||
      !values.controller ||
      !modalState.inputId
    )
      return;

    AdvancedTagMappingService.getRelevantModes(
      customerId,
      siteId,
      gatewayId,
      values.controller
    ).then((data: RelevantModeResponseBody[]) => {
      let labels: string[] = ["Don't restrict for certain modes", ''];
      let values: UserDefinedValueWithCustomId[] = labels?.map((label, index) => ({
        id: index === 0 ? RELEVANT_MODES.DO_NOT_RESTRICT : '',
        index: index === 0 ? -1 : -2,
        value: index === 0 ? -1 : -2,
        category: null,
        name: null,
        gatewayId: '',
        tagId: '',
      }));

      getRelevantModeValuesFromUserDefinedValues(data, labels, values);

      setRelevantModeState({
        labels: labels,
        values: values,
        loaded: true,
      });
    });
  }, [
    customerId,
    siteId,
    gatewayId,
    values.controller,
    modalState.open,
    modalState.inputId,
    getRelevantModeValuesFromUserDefinedValues,
  ]);

  useEffect(() => {
    if (!customerId || !siteId || !gatewayId || !modalState.inputId) return;

    reset(formData);

    if (modalState.open) {
      AdvancedTagMappingService.getById(customerId, siteId, gatewayId, modalState.inputId)
        .then((cInput: GatewayInput) => {
          let newFormValues: any = {};

          newFormValues = {
            controller: cInput.advancedTagMapping?.controllerId ?? '',
            allowedInputs: cInput.advancedTagMapping?.allowedInputs ?? ALLOWED_INPUTS.PREDEFINED,
            dataType: cInput.advancedTagMapping?.dataTypeTagMapping ?? DATA_TYPES.INTEGER,
            controllable: cInput.advancedTagMapping?.controllable
              ? CONTROLLABLE.YES
              : CONTROLLABLE.NO,
            minValue: cInput.advancedTagMapping?.minimumValue?.toString() ?? '',
            maxValue: cInput.advancedTagMapping?.maximumValue?.toString() ?? '',
            maxDecimal: cInput.advancedTagMapping?.maximumDecimal?.toString() ?? '',
            labellingType: cInput.labellingType ?? LabelingTypeEnum.LABEL,
            fallbackValue: cInput.fallbackValue ?? 'Undefined',
          };

          modalState.tagType.toLowerCase() !== TagType.MODE
            ? setShowRelevantMode(newFormValues.controllable === CONTROLLABLE.YES)
            : setShowRelevantMode(false);

          setValue('controller', newFormValues.controller);
          setValue('allowedInputs', newFormValues.allowedInputs);
          setValue('dataType', newFormValues.dataType);
          setValue('controllable', newFormValues.controllable);
          setValue('minValue', newFormValues.minValue);
          setValue('maxValue', newFormValues.maxValue);
          setValue('maxDecimal', newFormValues.maxDecimal);
          setValue('fallbackValue', newFormValues.fallbackValue);
          setValue('labellingType', newFormValues.labellingType);

          if (cInput.advancedTagMapping?.userDefinedValues) {
            const newRowData: ModalTableRowData[] = [];
            cInput.advancedTagMapping.userDefinedValues.forEach((value) => {
              if (value.index || value.index === 0) {
                newRowData.push({ index: value.index });
                setValue(`value-${value.index}`, value.value.toString());
                setValue(`name-${value.index}`, value.name ?? '');
                if (newFormValues.dataType !== DATA_TYPES.FLOAT) {
                  setValue(`category-${value.index}`, value.category ?? '');
                }
              }
            });
            newRowData.sort((a, b) => a.index - b.index);
            setRowData(newRowData);
          } else {
            setRowData((prevValue) => {
              const rowsToKeep = prevValue.slice(0, 3);
              return rowsToKeep;
            });
          }

          setCurrentInput({ value: cInput, loaded: true });
          if (cInput) setDataHasFullyLoaded(true);
        })
        .catch((error) => {
          setCurrentInput({ value: null, loaded: false });
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, siteId, gatewayId, modalState.tagType, modalState.open, modalState.inputId]);

  useEffect(() => {
    if (modalState.open && relevantModeState.loaded && currentInput.loaded) {
      const relevantMode = getRelevantModeIds(currentInput.value?.advancedTagMapping?.relevantMode);

      const relevantModeValue: string[] = [];
      relevantMode.forEach((id: string) => {
        const tempId = relevantModeState.values.find((value) => value.id === id)?.id;
        if (tempId) relevantModeValue.push(tempId);
      });
      let relevantModeFinalValue: string[] = [];
      relevantModeFinalValue = relevantModeValue.length > 0 ? relevantModeValue : [];

      if (relevantModeFinalValue.includes('') && relevantModeFinalValue.length === 1) {
        setValue('relevantMode', []);
      } else {
        setValue('relevantMode', relevantModeFinalValue);
      }

      setDataHasFullyLoaded(true);
    }
  }, [
    modalState.open,
    currentInput.loaded,
    relevantModeState.loaded,
    currentInput.value?.advancedTagMapping?.relevantMode,
    relevantModeState.values,
    setValue,
  ]);

  const clearAllTableCustomErrors = useCallback(() => {
    rowData.forEach((row) => {
      clearCustomErrors(`value-${row.index}`, '', true);
      clearCustomErrors(`category-${row.index}`, '', true);
      clearCustomErrors(`name-${row.index}`, '', true);
    });
  }, [rowData]);

  useEffect(() => {
    if (
      values.allowedInputs === ALLOWED_INPUTS.PREDEFINED &&
      (modalState.tagType.toLowerCase() === TagType.MODE ||
        modalState.tagType.toLowerCase() === TagType.STATE ||
        modalState.tagType.toLowerCase() === TagType.ALARM)
    ) {
      setDataTypeState({ value: dataTypes.modeStatePredefined, loaded: true });
    } else if (
      values.allowedInputs === ALLOWED_INPUTS.PREDEFINED &&
      (modalState.tagType.toLowerCase() === TagType.SETPOINT ||
        modalState.tagType.toLowerCase() === TagType.OUTPUT ||
        modalState.tagType.toLowerCase().includes(TagType.SENSOR) ||
        modalState.tagType.toLowerCase() === TagType.CALCULATED)
    ) {
      setDataTypeState({ value: dataTypes.setpointOutputPredefined, loaded: true });
    } else if (
      values.allowedInputs === ALLOWED_INPUTS.RANGE &&
      (modalState.tagType.toLowerCase() === TagType.MODE ||
        modalState.tagType.toLowerCase() === TagType.STATE)
    ) {
      setDataTypeState({ value: dataTypes.modeStateRange, loaded: true });
    } else if (
      values.allowedInputs === ALLOWED_INPUTS.RANGE &&
      (modalState.tagType.toLowerCase() === TagType.SETPOINT ||
        modalState.tagType.toLowerCase() === TagType.OUTPUT ||
        modalState.tagType.toLowerCase().includes(TagType.SENSOR) ||
        modalState.tagType.toLowerCase() === TagType.CALCULATED)
    ) {
      setDataTypeState({ value: dataTypes.setpointOutputRange, loaded: true });
    }
  }, [values.allowedInputs, dataTypes, modalState.tagType, reset]);

  useEffect(() => {
    if (values.dataType === DATA_TYPES.INTEGER) {
      addNewTableRows(2);
    } else {
      addNewTableRows(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalState.open) {
      setRowData([{ index: 0 }, { index: 1 }, { index: 2 }]);
      setRelevantModeState((prevValue) => ({ ...prevValue, loaded: false }));
      setCurrentInput((prevValue) => ({ ...prevValue, loaded: false }));
      setDataHasFullyLoaded(false);
      clearCustomErrors('maxValue', '', true);
      clearCustomErrors('minValue', '', true);
      reset();
    }
  }, [modalState.open, reset]);

  useEffect(() => {
    if (!isControllable) {
      clearErrors('controller');
    }
  }, [isControllable, clearErrors]);

  const prepareRelevantModeData = (data: AdvancedTagMapping) => {
    const relevantModesWithId = getEntireRelevantModes(data.relevantMode, relevantModeState);
    const relevantModes: UserDefinedValue[] = [];
    let relevantMode: UserDefinedValue | null = null;
    if (relevantModesWithId) {
      relevantModesWithId.forEach((mode) => {
        relevantMode = {
          value: mode.value,
          index: mode.index,
          category: mode.category,
          name: mode.name,
          gatewayId: mode.gatewayId,
          tagId: mode.tagId,
        };
        if (relevantMode) relevantModes.push(relevantMode);
      });
    }

    let relevantModeFormattedValue: string | UserDefinedValue[] | null = null;
    if (showRelevantMode && relevantModes) {
      relevantModeFormattedValue = relevantModes;

      if (relevantModes.length === 1) {
        if (relevantModes[0].value === -1) {
          relevantModeFormattedValue = RELEVANT_MODES.DO_NOT_RESTRICT;
        }
      }
    }

    return relevantModeFormattedValue;
  };

  const prepareDataForSubmit = (data: AdvancedTagMapping) => {
    const relevantModeFormattedValue = prepareRelevantModeData(data);

    const userDefinedValues =
      data.allowedInputs === ALLOWED_INPUTS.PREDEFINED
        ? getFormattedData(data, rowData, gatewayId, modalState.inputId, values.dataType)
        : [];
    const formattedData: FormattedSubmitData = {
      dataType: modalState.tagType.includes('Sensor')
        ? 'sensor_data'
        : modalState.tagType.toLowerCase(),
      displayName: modalState.tagDisplayName,
      fallbackValue: data.fallbackValue,
      labellingType: data.labellingType,
      advancedTagMapping: {
        controllerId: data.controller || null,
        allowedInputs: data.allowedInputs,
        dataTypeTagMapping: data.dataType,
        controllable: data.controllable === CONTROLLABLE.YES,
        relevantMode: relevantModeFormattedValue,
        minimumValue: data.allowedInputs === ALLOWED_INPUTS.RANGE ? +data.minValue : null,
        maximumValue: data.allowedInputs === ALLOWED_INPUTS.RANGE ? +data.maxValue : null,
        userDefinedValues: userDefinedValues,
        maximumDecimal: data.dataType === DATA_TYPES.FLOAT ? +data.maxDecimal : null,
      },
    };

    return { formattedData: formattedData, userDefinedValues: userDefinedValues };
  };

  const setUpdatedInputsPostSubmit = (userDefinedValues: UserDefinedValue[]) => {
    modalState.setUpdatedInputs((prevValue) => {
      let newValue: UpdatedInput[] = [];
      prevValue.forEach((val) => {
        newValue.push(val);
      });

      const newUpdatedInput: UpdatedInput = {
        inputId: modalState.inputId,
        controllerId: values.controller,
        userDefinedValues: userDefinedValues,
        minimumValue: values.minValue,
        maximumValue: values.maxValue,
        dataType: values.dataType,
        controllable: values.controllable === CONTROLLABLE.YES,
      };

      const index = newValue.findIndex(
        (obj: UpdatedInput) => obj.inputId === newUpdatedInput.inputId
      );

      if (index !== -1) {
        newValue[index] = newUpdatedInput;
      } else {
        newValue.push(newUpdatedInput);
      }

      return newValue;
    });
  };

  const controllerId = watch('controller');

  useEffect(() => {
    const checkControllerType = async (controllerId: string) => {
      const controllerDetails = modalState.controllers?.find((ctrl) => ctrl.id === controllerId);
      if (controllerDetails) {
        setControllerType(controllerDetails.controllerType);
      }
    };

    if (controllerId) {
      checkControllerType(controllerId);
    }
  }, [controllerId, modalState.controllers]);

  const onSubmit = async (data: AdvancedTagMapping) => {
    if (!customerId || !siteId || Object.keys(customErrors).length > 0) return;

    const { userDefinedValues, formattedData } = prepareDataForSubmit(data);

    try {
      const saveAdvancedTagMapping = isIntegralert
        ? AdvancedTagMappingService.updateIntegralertAdvancedTagMapping
        : isStreamMLite
        ? AdvancedTagMappingService.updateAdvancedTagMapping
        : AdvancedTagMappingService.update;

      await saveAdvancedTagMapping(
        formattedData,
        customerId,
        siteId,
        gatewayId,
        modalState.inputId
      );

      setUpdatedInputsPostSubmit(userDefinedValues);

      enqueueSnackbar(ATM_SUBMIT_SUCCESS_MESSAGE, {
        variant: 'success',
      });
      handleCloseModal();
    } catch (error) {
      enqueueSnackbar(error?.message ?? error ?? ATM_SUBMIT_FAILURE_MESSAGE, {
        variant: 'error',
      });
      console.error(error);
    }
  };

  const addNewTableRows = (times: number) => {
    let newFormValues: any = {};
    const newIndices: number[] = [];

    const rowWithLargestIndex = rowData.reduce((acc, currRow) => {
      if (currRow.index > acc.index) {
        return currRow;
      } else {
        return acc;
      }
    }, rowData[0]);

    const lastOldIndex: number = rowWithLargestIndex?.index;

    for (let i = 1; i <= times; i++) {
      const newIndex = lastOldIndex + i;
      newIndices.push(newIndex);

      newFormValues[`value-${newIndex}`] = '';
      newFormValues[`category-${newIndex}`] = '';
      newFormValues[`name-${newIndex}`] = '';
    }

    setFormData((prevValue) => ({ ...prevValue, ...newFormValues }));

    setRowData((prevValue: ModalTableRowData[]) => {
      let tempValue: ModalTableRowData[] = [];
      prevValue.forEach((value) => {
        tempValue.push(value);
      });

      newIndices.forEach((newIndex) => tempValue.push({ index: newIndex }));
      return tempValue;
    });
  };

  const clearCustomErrors = (
    name: string,
    specificErrorValue: string,
    clearEntireKey: boolean = false
  ) => {
    setCustomErrors((prevValue) => {
      const newValue = { ...prevValue };

      if (clearEntireKey) {
        delete newValue[name];
        return newValue;
      }

      if (newValue[name]) {
        newValue[name].message = newValue[name].message.filter(
          (message) => message !== specificErrorValue
        );

        if (newValue[name].message.length === 0) {
          delete newValue[name];
        }
      }

      return newValue;
    });
  };

  const cleanseAllCustomErrors = () => {
    clearAllTableCustomErrors();
    clearCustomErrors('maxValue', '', true);
    clearCustomErrors('minValue', '', true);
  };

  const handleCloseModal = () => {
    cleanseAllCustomErrors();
    onClose((prevValue) => ({ ...prevValue, open: false }));
  };

  const handleDropdownChange = (newValue: string | string[] | null, name: string) => {
    if (name === 'allowedInputs') {
      const currentController = values.controller;

      reset();
      cleanseAllCustomErrors();
      setShowRelevantMode(false);
      setDataTypeState((prevValue) => ({ ...prevValue, loaded: false }));
      if (newValue === ALLOWED_INPUTS.PREDEFINED) {
        setRowData([{ index: 0 }, { index: 1 }, { index: 2 }]);
      }
      setValue('controller', currentController);
      setValue('relevantMode', [RELEVANT_MODES.DO_NOT_RESTRICT]);
      setValue('dataType', DATA_TYPES.INTEGER);
    }

    if (name === 'controllable') {
      modalState.tagType.toLowerCase() !== TagType.MODE
        ? setShowRelevantMode(newValue === CONTROLLABLE.YES)
        : setShowRelevantMode(false);
    }

    if (name === 'dataType') {
      cleanseAllCustomErrors();
      const resetFieldsCommon = ['name', 'category'];
      const rowDataCount: { [key: string]: number } = {
        [DATA_TYPES.BOOLEAN]: 2,
        [DATA_TYPES.INTEGER]: 3,
        [DATA_TYPES.FLOAT]: 1,
      };

      setRowData(
        Array.from({ length: rowDataCount[newValue as string] || 0 }, (_, index) => ({
          index,
        }))
      );

      if (newValue === DATA_TYPES.BOOLEAN || newValue === DATA_TYPES.FLOAT) {
        reset(getUpdatedFormValues(values, newValue, rowData));
      }

      if (
        values.dataType === DATA_TYPES.FLOAT &&
        newValue !== DATA_TYPES.FLOAT &&
        values.allowedInputs === ALLOWED_INPUTS.RANGE
      ) {
        setValue('maxDecimal', '0');
        const newMaxValue = validateValueChange(values.maxValue, true, 0) ?? null;
        const newMinValue = validateValueChange(values.minValue, true, 0) ?? null;
        if (newMaxValue || newMaxValue === '') {
          setValue('maxValue', newMaxValue);
          handleMinMaxValueValidation('maxValue', newMaxValue, newMinValue);
        }

        if (newMinValue || newMinValue === '') {
          setValue('minValue', newMinValue);
          handleMinMaxValueValidation('minValue', newMinValue, newMaxValue);
        }
      }

      for (let i = 0; i < (rowDataCount[newValue as string] || 0); i++) {
        resetFieldsCommon.forEach((field) => setValue(`${field}-${i}`, ''));
        if (newValue === DATA_TYPES.BOOLEAN) {
          setValue(`value-${i}`, i.toString());
          if (modalState.tagType?.toLowerCase() === 'state') {
            setValue(`category-${i}`, i ? 'ON' : 'OFF');
            setValue(`name-${i}`, i ? 'On' : 'Off');
          }
        } else {
          setValue(`value-${i}`, '');
        }
      }
    }

    if (name !== 'relevantMode') {
      setValue(name, newValue as string);
      if (newValue) {
        clearErrors(name);
      }
    } else {
      const addedValue = (newValue as string[]).find(
        (value) => !values.relevantMode.includes(value)
      );
      if (addedValue && addedValue === RELEVANT_MODES.DO_NOT_RESTRICT) {
        setValue(name, [RELEVANT_MODES.DO_NOT_RESTRICT]);
      } else if (
        addedValue &&
        addedValue !== RELEVANT_MODES.DO_NOT_RESTRICT &&
        (newValue as string[]).includes(RELEVANT_MODES.DO_NOT_RESTRICT)
      ) {
        setValue(
          name,
          (newValue as string[]).filter((newV) => newV !== RELEVANT_MODES.DO_NOT_RESTRICT)
        );
      } else {
        setValue(name, newValue as string[]);
      }
      clearErrors('relevantMode');
    }
  };

  const handleRemoveRow = (index: number) => {
    const rowsToKeep = rowData.filter((row) => row.index !== index);

    const fieldsToRemove: string[] = [`value-${index}`, `name-${index}`];
    if (values.dataType !== DATA_TYPES.FLOAT) fieldsToRemove.push(`category-${index}`);
    reset(removeDynamicFields(values, fieldsToRemove));
    clearCustomErrors(`value-${index}`, '', true);
    setRowData(rowsToKeep);

    const newValues: { key: number; value: string }[] = [];
    rowsToKeep.forEach((row) => {
      const newPredefinedValue = validateValueChange(
        values[`value-${row.index}`] as string,
        true,
        values.dataType === DATA_TYPES.FLOAT ? +values.maxDecimal : 0
      );
      if (newPredefinedValue) newValues.push({ key: row.index, value: newPredefinedValue });
    });

    newValues.forEach((value) => {
      handleAreTableValuesUnique(value.value, value.key, +values.maxDecimal, newValues);
    });
  };

  const handleAreTableValuesUnique = (
    value: string,
    index: number,
    maxDecimal: number | null = null,
    newValues: { key: number; value: string }[] | null = null
  ) =>
    areTableValuesUnique(
      value,
      index,
      values,
      setValue,
      customErrors,
      setCustomErrors,
      clearCustomErrors,
      clearErrors,
      rowData,
      tableValueErrors,
      setTableValueErrors,
      maxDecimal ? +maxDecimal : +values.maxDecimal,
      newValues
    );

  const handleMinMaxValueValidation = (
    name: string,
    newValue: string,
    newOppositeValue: string | null = null
  ) => {
    validateMinMaxValueChange(
      name,
      newValue,
      values,
      customErrors,
      setCustomErrors,
      clearCustomErrors,
      clearErrors,
      newOppositeValue
    );
  };

  const handleValueChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    name: string
  ) => {
    let newValue = validateValueChange(
      event.currentTarget.value,
      true,
      values.dataType === DATA_TYPES.FLOAT ? +values.maxDecimal : 0
    );

    if (newValue === null || newValue === undefined) return;

    handleMinMaxValueValidation(name, newValue);

    setValue(name, newValue);
  };

  const handleMaxDecimalChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const newValue = validateValueChange(event.currentTarget.value, false, 0);
    if (newValue === null || newValue === undefined) return;
    setValue('maxDecimal', newValue);
    if (newValue !== '') {
      clearErrors('maxDecimal');
    }

    if (values.allowedInputs === ALLOWED_INPUTS.RANGE) {
      const newMinValue = validateValueChange(
        values.minValue,
        true,
        values.dataType === DATA_TYPES.FLOAT ? +newValue : 0
      );
      const newMaxValue = validateValueChange(
        values.maxValue,
        true,
        values.dataType === DATA_TYPES.FLOAT ? +newValue : 0
      );

      if (newMinValue || newMinValue === '') {
        setValue('minValue', newMinValue);
        handleMinMaxValueValidation('minValue', newMinValue, newMaxValue);
      }
      if (newMaxValue || newMaxValue === '') {
        setValue('maxValue', newMaxValue);
        handleMinMaxValueValidation('maxValue', newMaxValue, newMinValue);
      }
    } else {
      const newValues: { key: number; value: string }[] = [];
      rowData.forEach((row) => {
        const newPredefinedValue = validateValueChange(
          values[`value-${row.index}`] as string,
          true,
          values.dataType === DATA_TYPES.FLOAT ? +newValue : 0
        );

        if (newPredefinedValue) newValues.push({ key: row.index, value: newPredefinedValue });
      });

      newValues.forEach((value) => {
        handleAreTableValuesUnique(value.value, value.key, +newValue, newValues);
      });
    }
  };

  const renderErrorElement = (messages: string[]) => (
    <Alert severity="error" style={{ margin: '8px 0' }}>
      {messages?.map((message) => (
        <Typography key={message}>{message}</Typography>
      ))}
    </Alert>
  );

  const renderErrorView = () => {
    const hasMandatoryError = Object.keys(errors).length !== 0;
    const hasCustomError = Object.keys(customErrors).length !== 0;

    const uniqueErrorMessages: string[][] = [];
    Object.values(customErrors).forEach((error) => {
      if (error?.message) {
        uniqueErrorMessages.push(error.message);
      }
    });

    const flatArray = uniqueErrorMessages.flat();
    const uniqueSet = new Set(flatArray);
    const uniqueArray = Array.from(uniqueSet);

    const customErrorElement = renderErrorElement(uniqueArray);
    const mandatoryErrorElement = renderErrorElement([MANDATORY_FIELDS_VALIDATION_ERROR_MESSAGE]);

    if (hasMandatoryError && hasCustomError) {
      return (
        <Grid item xs={12}>
          {mandatoryErrorElement}
          {customErrorElement}
        </Grid>
      );
    } else if (hasMandatoryError) {
      return (
        <Grid item xs={12}>
          {mandatoryErrorElement}
        </Grid>
      );
    } else if (hasCustomError) {
      return (
        <Grid item xs={12}>
          {customErrorElement}
        </Grid>
      );
    }

    return null;
  };

  const renderTags = (value: any, getTagProps: AutocompleteRenderGetTagProps) =>
    value
      .filter((option: any) => option !== '')
      .map((option: any, index: number) => {
        const { values: tempValues, labels } = relevantModeState;
        const foundValue = tempValues.find((value) => value.id === option);
        if (!foundValue) return '';
        const foundIndex = tempValues.indexOf(foundValue);

        return (
          <Chip
            label={labels[foundIndex]}
            {...getTagProps({ index })}
            key={option}
            onDelete={() => {
              setValue(
                'relevantMode',
                values.relevantMode.filter((item) => item !== option)
              );
            }}
          />
        );
      });

  const renderRelevantMode = () => {
    const gridElement = (
      <Grid item {...calculateGridPropsRelevantMode(values, isMobile)} style={{ width: 'inherit' }}>
        <RHFAutocomplete
          name="relevantMode"
          multiple
          options={relevantModeState.values?.map((value) => value.id)}
          getOptionLabel={(option: string) => {
            const { values, labels } = relevantModeState;
            const foundValue = values.find((value) => value.id === option);
            if (!foundValue) return '';
            const index = values.indexOf(foundValue);
            return `${labels[index]}`;
          }}
          filterOptions={(options: string[], state: any) =>
            filterOptions(options, state, relevantModeState.labels)
          }
          loading={!relevantModeState.loaded}
          disableClearable
          disabled={hasReadAccess}
          isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
          rules={{
            required: `Relevant Mode is required!`,
          }}
          onChange={(_, newValue) => handleDropdownChange(newValue, 'relevantMode')}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Relevant Mode*"
              error={!!errors.relevantMode}
              helperText={errors.relevantMode?.message}
              inputProps={{
                ...params.inputProps,
                'data-sm': 'relevant-mode-input',
              }}
            />
          )}
          renderTags={(value, getTagProps) => renderTags(value, getTagProps)}
        />
      </Grid>
    );
    return values.allowedInputs !== ALLOWED_INPUTS.RANGE && values.dataType !== DATA_TYPES.FLOAT ? (
      <Stack style={{ width: '100%', marginTop: '16px', marginLeft: '16px' }} alignItems="center">
        {gridElement}
      </Stack>
    ) : (
      <>{gridElement}</>
    );
  };

  const renderMaximalDecimal = () => {
    const gridElement = (
      <Grid
        item
        {...calculateGridPropsValues(showRelevantMode, values, isMobile)}
        style={{ width: 'inherit' }}
      >
        <RHFTextField
          name={`maxDecimal`}
          testId="max-decimal-field"
          placeholder="Enter value"
          rules={{
            required: `Maximum decimal is required!`,
          }}
          disabled={hasReadAccess}
          error={!!errors.maxDecimal}
          helperText={errors.maxDecimal?.message}
          label="Max Decimals*"
          type="text"
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            handleMaxDecimalChange(event)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SimpleTooltip content={MAX_DECIMAL_FIELD_TOOLTIP} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    );
    return values.allowedInputs !== ALLOWED_INPUTS.RANGE &&
      values.dataType === DATA_TYPES.FLOAT &&
      !showRelevantMode ? (
      <Stack style={{ width: '100%', marginTop: '16px', marginLeft: '16px' }} alignItems="center">
        {gridElement}
      </Stack>
    ) : (
      <>{gridElement}</>
    );
  };

  return (
    <AdvancedTagMappingPopupDialog open={modalState.open} onClose={handleCloseModal} maxWidth="lg">
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        <DialogTitle>Advanced Tag Mapping</DialogTitle>
        <Divider />
        {dataHasFullyLoaded ? (
          <DialogContent
            sx={{
              marginBottom: '32px',
              minHeight: '300px',
            }}
          >
            <FormProvider onSubmit={handleSubmit(onSubmit)} methods={methods}>
              <Grid container spacing={2} sx={{ mt: '16px' }}>
                <Grid item xs={isMobile ? 12 : 4}>
                  <TextField
                    name="tagNameATM"
                    label="Tag Name"
                    disabled
                    style={{ width: '100%' }}
                    value={modalState.tagDisplayName}
                  />
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                  <TextField
                    name="tagType"
                    label="Tag Type"
                    disabled
                    style={{ width: '100%' }}
                    value={modalState.tagType}
                  />
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                  {!isMobile ? (
                    <RHFAutocomplete
                      name="controllable"
                      options={controllable.values}
                      getOptionLabel={(option: string) => {
                        const { values, labels } = controllable;
                        const index = values.indexOf(option);
                        return `${labels[index]}`;
                      }}
                      filterOptions={(options: string[], state: any) => {
                        const { labels } = controllable;
                        return filterOptions(options, state, labels);
                      }}
                      disableClearable
                      disabled={hasReadAccess || noControllers || disableControllerFields}
                      isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
                      rules={{
                        required: `Controllable is required!`,
                      }}
                      onChange={(_, newValue) => handleDropdownChange(newValue, 'controllable')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Controllable*"
                          error={!!errors.controllable}
                          helperText={
                            controllableHelperText ??
                            (noControllers ? noControllersHelperText : errors.controllable?.message)
                          }
                          inputProps={{
                            ...params.inputProps,
                            'data-sm': 'controllable-input',
                          }}
                        />
                      )}
                    />
                  ) : (
                    <RHFSelect
                      name="controllable"
                      options={controllable.values?.map((value) => ({
                        label: upperSnakeCaseToTitleCase(value),
                        value: value,
                      }))}
                      disabled={hasReadAccess || noControllers || disableControllerFields}
                      onChange={(event) =>
                        handleDropdownChange(event.target.value as string, 'controllable')
                      }
                      label="Controllable*"
                      testId="controllable-input"
                      helperText={
                        controllableHelperText ??
                        (noControllers ? noControllersHelperText : undefined)
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                  {!isMobile ? (
                    <RHFAutocomplete
                      name="allowedInputs"
                      options={allowedInputsValues}
                      getOptionLabel={(option: string) => {
                        const { values, labels } = allowedInputs;
                        const index = values.indexOf(option);
                        return `${labels[index]}`;
                      }}
                      filterOptions={(options: string[], state: any) => {
                        const { labels } = allowedInputs;
                        return filterOptions(options, state, labels);
                      }}
                      disableClearable
                      disabled={hasReadAccess || modalState.tagType.toLowerCase() === TagType.ALARM}
                      isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
                      rules={{
                        required: `Allowed Inputs is required!`,
                      }}
                      onChange={(_, newValue) => handleDropdownChange(newValue, 'allowedInputs')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Allowed Inputs*"
                          error={!!errors.allowedInputs}
                          helperText={errors.allowedInputs?.message}
                          inputProps={{
                            ...params.inputProps,
                            'data-sm': 'allowed-inputs-input',
                          }}
                        />
                      )}
                    />
                  ) : (
                    <RHFSelect
                      name="allowedInputs"
                      options={allowedInputsValues?.map((value) => ({
                        label: upperSnakeCaseToTitleCase(value),
                        value: value,
                      }))}
                      disabled={hasReadAccess}
                      onChange={(event) =>
                        handleDropdownChange(event.target.value as string, 'allowedInputs')
                      }
                      label="Allowed Inputs*"
                      testId="allowed-inputs-input"
                    />
                  )}
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                  {!isMobile ? (
                    <RHFAutocomplete
                      name="dataType"
                      options={dataTypeState.value.values}
                      getOptionLabel={(option: string) => {
                        const { values, labels } = dataTypeState.value;
                        const index = values.indexOf(option);
                        return `${labels[index]}`;
                      }}
                      filterOptions={(options: string[], state: any) => {
                        const { labels } = dataTypeState.value;
                        return filterOptions(options, state, labels);
                      }}
                      disableClearable
                      disabled={hasReadAccess}
                      isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
                      rules={{
                        required: `Data Type is required!`,
                      }}
                      loading={!dataTypeState.loaded}
                      onChange={(_, newValue) => handleDropdownChange(newValue, 'dataType')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Data Type*"
                          error={!!errors.dataType}
                          helperText={errors.dataType?.message}
                          inputProps={{
                            ...params.inputProps,
                            'data-sm': 'data-type-input',
                          }}
                        />
                      )}
                    />
                  ) : (
                    <RHFSelect
                      name="dataType"
                      options={
                        !dataTypeState.loaded
                          ? [{ label: '', value: 'loading' }]
                          : dataTypeState.value.values?.map((value) => ({
                              label: upperSnakeCaseToTitleCase(value),
                              value: value,
                            }))
                      }
                      disabled={hasReadAccess}
                      isLoading={!dataTypeState.loaded}
                      onChange={(event) =>
                        handleDropdownChange(event.target.value as string, 'dataType')
                      }
                      label="Data Type*"
                      testId="data-type-input"
                    />
                  )}
                </Grid>

                <Grid item xs={isMobile ? 12 : 4}>
                  {!isMobile ? (
                    <RHFAutocomplete
                      name="controller"
                      options={[
                        ...[''],
                        ...(modalState.controllers?.map((controller) => controller.id) ?? []),
                      ]}
                      getOptionLabel={(option: string) => {
                        if (option === '') return '';
                        let values: string[] = [];
                        let labels: string[] = [];
                        modalState.controllers?.forEach((controller: Controller) => {
                          values.push(controller.id);
                          labels.push(controller.controllerName);
                        });
                        const index = values.indexOf(option);
                        return `${labels[index]}`;
                      }}
                      filterOptions={(options: string[], state: any) => {
                        let labels: string[] = [''];
                        modalState.controllers?.forEach((controller: Controller) => {
                          labels.push(controller.controllerName);
                        });
                        return filterOptions(options, state, labels);
                      }}
                      disabled={hasReadAccess || noControllers || disableControllerFields}
                      rules={{
                        required: isControllable ? `Controller is required!` : false,
                      }}
                      isOptionEqualToValue={(option, value) => (!option ? true : option === value)}
                      onChange={(_, newValue) => handleDropdownChange(newValue, 'controller')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Controller${isControllable ? '*' : ''}`}
                          error={!!errors.controller}
                          helperText={errors.controller?.message}
                          inputProps={{
                            ...params.inputProps,
                            'data-sm': 'controller-input',
                          }}
                        />
                      )}
                    />
                  ) : (
                    <RHFSelect
                      name="controller"
                      optional={!isControllable}
                      rules={{
                        required: isControllable ? 'Controller is required!' : false,
                      }}
                      options={
                        modalState.controllers?.map((controller) => ({
                          label: controller.controllerName,
                          value: controller.id,
                        })) ?? []
                      }
                      disabled={hasReadAccess || noControllers || disableControllerFields}
                      label={`Controller${isControllable ? '*' : ''}`}
                      onChange={(event) =>
                        handleDropdownChange(event.target.value as string, 'controller')
                      }
                      testId="controller-input"
                    />
                  )}
                </Grid>

                <LabelOptionsForm
                  isMobile={isMobile}
                  handleDropdownChange={handleDropdownChange}
                  hasReadAccess={hasReadAccess}
                />
                {values.dataType === DATA_TYPES.FLOAT ? renderMaximalDecimal() : null}
                {showRelevantMode ? renderRelevantMode() : null}

                {values.allowedInputs === ALLOWED_INPUTS.PREDEFINED ? (
                  <Grid item xs={12}>
                    <AdvancedTagMappingTable
                      rowData={rowData}
                      addNewRow={addNewTableRows}
                      dataType={values.dataType}
                      tagType={modalState.tagType}
                      handleRemoveRow={handleRemoveRow}
                      areTableValuesUnique={handleAreTableValuesUnique}
                      setValue={setValue}
                      values={values}
                      errors={errors}
                      customErrors={customErrors}
                      clearErrors={clearErrors}
                      controllerType={controllerType || ''}
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid item {...calculateGridPropsValues(showRelevantMode, values, isMobile)}>
                      <RHFTextField
                        name={`minValue`}
                        testId="min-value-field"
                        placeholder="Enter value"
                        rules={{
                          required: `Minimum value is required!`,
                        }}
                        disabled={hasReadAccess}
                        error={!!errors.minValue || !!customErrors?.minValue}
                        customHelperText={
                          errors.minValue?.message || customErrors?.minValue?.message[0]
                        }
                        label="Minimum Value*"
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                        ) => handleValueChange(event, 'minValue')}
                        type="text"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SimpleTooltip content={RANGE_FIELD_TOOLTIP} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item {...calculateGridPropsValues(showRelevantMode, values, isMobile)}>
                      <RHFTextField
                        name={`maxValue`}
                        testId="max-value-field"
                        placeholder="Enter value"
                        rules={{
                          required: `Maximum value is required!`,
                        }}
                        disabled={hasReadAccess}
                        error={!!errors.maxValue || !!customErrors?.maxValue}
                        customHelperText={
                          errors.maxValue?.message || customErrors?.maxValue?.message[0]
                        }
                        label="Maximum Value*"
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                        ) => handleValueChange(event, 'maxValue')}
                        type="text"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SimpleTooltip content={RANGE_FIELD_TOOLTIP} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                {renderErrorView()}

                <Stack
                  justifyContent="flex-end"
                  flexDirection={'row'}
                  sx={{ mt: 3, width: '100%' }}
                >
                  {!hasReadAccess ? (
                    <Box>
                      <Button
                        onClick={handleCloseModal}
                        variant="outlined"
                        sx={{ mr: 1 }}
                        data-sm="cancel-button"
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        type="submit"
                        disabled={
                          Object.keys(customErrors).length > 0 || Object.keys(errors).length > 0
                        }
                        variant="contained"
                        loading={isSubmitting}
                        data-sm={'add-asset-button'}
                      >
                        Apply Changes
                      </LoadingButton>
                    </Box>
                  ) : (
                    <Button
                      onClick={handleCloseModal}
                      variant="outlined"
                      sx={{ mr: 1 }}
                      data-sm="cancel-button"
                    >
                      Close
                    </Button>
                  )}
                </Stack>
              </Grid>
            </FormProvider>
          </DialogContent>
        ) : (
          <DialogContent
            sx={{
              marginBottom: '32px',
              minHeight: '300px',
            }}
          >
            <AdvancedTagMappingSkeleton />
          </DialogContent>
        )}
      </Box>
    </AdvancedTagMappingPopupDialog>
  );
};
